import { Link } from "@tanstack/react-router";
import type { DashboardWorkspace } from "@/api/types";
import { cn } from "@/lib/cn";
import { dashboardSectionPath, useDashboardActiveSlug } from "@/lib/dashboard-context";
import { DashboardWorkspaceSwitcher } from "./DashboardWorkspaceSwitcher";
import { dashboardNavItemsForRole } from "./dashboard-nav";
import { ProAvatar } from "./ui";

interface DashboardSidebarProps {
  businessName?: string;
  workspaces?: DashboardWorkspace[];
  userName?: string;
  userRole?: string;
  collapsed?: boolean;
  switchingWorkspace?: boolean;
  onWorkspaceSelect?: (workspace: DashboardWorkspace) => void;
  onListNewBusiness?: () => void;
}

// fallow-ignore-next-line complexity
function formatRoleLabel(role: string): string {
  return role
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function DashboardSidebar({
  businessName = "Your Business",
  workspaces = [],
  userName = "Team member",
  userRole = "owner",
  collapsed = false,
  switchingWorkspace = false,
  onWorkspaceSelect,
  onListNewBusiness,
}: DashboardSidebarProps) {
  const navItems = dashboardNavItemsForRole(userRole);
  const currentSlug = useDashboardActiveSlug();

  return (
    <aside
      className={cn(
        "sidebar flex h-[var(--visual-vh,100vh)] flex-col border-r border-pro-border bg-pro-bg px-3 py-5",
        collapsed ? "w-[var(--sidebar-w-collapsed)] items-center px-2" : "w-[var(--sidebar-w)]",
      )}
    >
      <div className={cn("mb-5 flex w-full items-center", collapsed ? "justify-center" : "")}>
        <DashboardWorkspaceSwitcher
          businessName={businessName}
          workspaces={workspaces}
          collapsed={collapsed}
          switching={switchingWorkspace}
          onSelect={(workspace) => onWorkspaceSelect?.(workspace)}
          onListNewBusiness={() => onListNewBusiness?.()}
        />
      </div>

      {!collapsed && (
        <div className="px-3 pb-1.5 font-mono text-[9px] font-medium uppercase tracking-[0.14em] text-pro-text-faint">
          Workspace
        </div>
      )}

      <nav className="flex flex-col gap-0.5">
        {navItems.map((item) => {
          const to = currentSlug ? dashboardSectionPath(currentSlug, item.section) : item.to;

          return (
            <Link key={item.to} to={to} preload="render" className="block">
              {({ isActive }) => (
                <span
                  className={cn(
                    "sb-link relative flex items-center gap-2.5 rounded-[var(--pro-r-sm)] px-3 py-2 text-[13px] font-medium transition-colors duration-[120ms]",
                    isActive ? "active text-pro-text" : "text-pro-text-muted",
                    collapsed && "sb-link-collapsed size-10 justify-center rounded-[14px] p-0",
                  )}
                >
                  {isActive && !collapsed && (
                    <span className="absolute top-1/2 left-0 h-[18px] w-[3px] -translate-y-1/2 rounded-r-sm bg-lime" />
                  )}
                  <span className={cn("shrink-0 opacity-80", isActive && "opacity-100")}>
                    <item.icon className="size-[18px] stroke-[1.8]" />
                  </span>
                  {!collapsed && <span>{item.label}</span>}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div
        className={cn(
          "mt-auto flex h-16 w-full items-center gap-2.5 border-t border-pro-border px-3",
          collapsed && "h-16 justify-center px-0 pt-0",
        )}
      >
        <ProAvatar name={userName} seed={userName} size={32} />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12px] font-semibold text-pro-text">{userName}</div>
            <div className="truncate text-[10px] text-pro-text-muted">
              {formatRoleLabel(userRole)}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

export { DashboardSidebar, type DashboardSidebarProps };
